import { theme } from 'infra/theme'
import React, { useEffect } from 'react'
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
  YAxis,
} from 'recharts'
import {
  SubSectionTitle,
  SubSectionWrapper,
} from 'components/startup/startup-styles'
import { BaseText, SmallText } from 'components/common/text'
import { Column, Row } from 'components/common/layout'
import styled from 'styled-components'
import dayjs from 'dayjs'
import {
  ValueType,
  NameType,
} from 'recharts/types/component/DefaultTooltipContent'
import { BtnSize, BtnType, Button } from 'components/common/button'
import { CHANNEL_TALK_URL } from 'infra/constants'
import { useIsDesktop } from 'utils/hooks'

const SERVICE_KEY =
  'kh7W5vhdjrdpAVRskkP1iFWF1GeJ8Hz1JIu2lHTERC9zn%2B48x8J0crDbFmMj1e8pCJDD8Bjikiu6digT9GFFwQ%3D%3D'
const PAGE_NO = 1
const NUM_OF_ROWS = 10
const DATA_TYPE = 'json'

// 사업장 기본정보
type BaseItem = {
  bzowrRgstNo: string
  dataCrtYm: string
  ldongAddrMgplDgCd: string
  ldongAddrMgplSgguCd: string
  ldongAddrMgplSgguEmdCd: string
  seq: number
  wkplJnngStcd: string
  wkplNm: string
  wkplRoadNmDtlAddr: string
  wkplStylDvcd: string
}

// 상세정보 API 모델
type DetailItem = {
  adptDt: string
  bzowrRgstNo: string
  crrmmNtcAmt: string
  jnngpCnt: number
  ldongAddrMgplDgCd: string
  ldongAddrMgplSgguCd: string
  ldongAddrMgplSgguEmdCd: string
  scsnDt: string
  vldtVlKrnNm: string
  wkplIntpCd: string
  wkplJnngStcd: string
  wkplNm: string
  wkplRoadNmDtlAddr: string
  wkplStylDvcd: string
}

// 기간별 입퇴사자 API 모델
type PeriodStatsItem = {
  lssJnngpCnt: number // 퇴사자
  nwAcqzrCnt: number // 입사자
}

type ApiResponse = {
  response: {
    header: {
      resultCode: string
      resultMsg: string
    }
    body: {
      items: {
        item: BaseItem[]
      }
      pageNo: number
      numOfRows: number
      totalCount: number
    }
  }
}

export const StartupChart: React.FC<{
  startupName: string
  businessNumber: number
}> = ({ startupName, businessNumber }) => {
  const isDesktop = useIsDesktop()
  const [items, setItems] = React.useState<BaseItem[]>([])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [totalEmp, setTotalEmp] = React.useState<number | null>(null)
  const [periodStats, setPeriodStats] = React.useState<
    Record<number, { join: number; leave: number }>
  >({})
  const [detailStats, setDetailStats] = React.useState<Record<number, number>>(
    {},
  )

  useEffect(() => {
    const API_URL = `https://apis.data.go.kr/B552015/NpsBplcInfoInqireServiceV2/getBassInfoSearchV2?serviceKey=${SERVICE_KEY}&wkplNm=${encodeURIComponent(
      startupName,
    )}&bzowrRgstNo=${businessNumber
      .toString()
      .slice(
        0,
        6,
      )}&pageNo=${PAGE_NO}&numOfRows=${NUM_OF_ROWS}&dataType=${DATA_TYPE}`
    fetch(API_URL)
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        if (!data.response.body.items.item) return
        setItems(data.response.body.items.item)

        // 가장 최근 seq로 상세정보 요청
        const latestSeq = data.response.body.items.item[0]?.seq
        if (latestSeq) {
          const DETAIL_SERVICE_KEY = SERVICE_KEY
          const DETAIL_PAGE_NO = 1
          const DETAIL_NUM_OF_ROWS = 10
          const DETAIL_DATA_TYPE = 'json'
          const DETAIL_URL = `https://apis.data.go.kr/B552015/NpsBplcInfoInqireServiceV2/getDetailInfoSearchV2?serviceKey=${DETAIL_SERVICE_KEY}&seq=${latestSeq}&pageNo=${DETAIL_PAGE_NO}&numOfRows=${DETAIL_NUM_OF_ROWS}&dataType=${DETAIL_DATA_TYPE}`
          fetch(DETAIL_URL)
            .then((res) => res.json())
            .then(
              (detail: {
                response: {
                  body: {
                    items: {
                      item: DetailItem[]
                    }
                  }
                }
              }) => {
                const empCnt = detail.response.body.items.item[0]?.jnngpCnt
                setTotalEmp(empCnt ?? null)
              },
            )
            .catch(() => setTotalEmp(null))
        }

        // 각 기간별 입/퇴사자 및 전체 인원 요청
        const fetchPeriodAndDetailStats = async () => {
          const stats: Record<number, { join: number; leave: number }> = {}
          const detailStats: Record<number, number> = {}
          await Promise.all(
            data.response.body.items.item.map(async (item) => {
              // 입퇴사자
              const PERIOD_URL = `https://apis.data.go.kr/B552015/NpsBplcInfoInqireServiceV2/getPdAcctoSttusInfoSearchV2?serviceKey=${SERVICE_KEY}&seq=${item.seq}&dataCrtYm=${item.dataCrtYm}&pageNo=1&numOfRows=10&dataType=json`
              try {
                const res = await fetch(PERIOD_URL)
                const json: {
                  response: {
                    body: {
                      items: {
                        item: PeriodStatsItem[]
                      }
                    }
                  }
                } = await res.json()
                const periodItem = json?.response?.body?.items?.item?.[0]
                stats[item.seq] = {
                  join: periodItem?.nwAcqzrCnt ?? 0,
                  leave: periodItem?.lssJnngpCnt ?? 0,
                }
              } catch {
                stats[item.seq] = { join: 0, leave: 0 }
              }

              // 상세 전체 인원
              const DETAIL_URL = `https://apis.data.go.kr/B552015/NpsBplcInfoInqireServiceV2/getDetailInfoSearchV2?serviceKey=${SERVICE_KEY}&seq=${item.seq}&pageNo=1&numOfRows=10&dataType=json`
              try {
                const res = await fetch(DETAIL_URL)
                const json: {
                  response: {
                    body: {
                      items: {
                        item: DetailItem[]
                      }
                    }
                  }
                } = await res.json()
                const detailItem = json?.response?.body?.items?.item?.[0]
                detailStats[item.seq] = detailItem?.jnngpCnt ?? 0
              } catch {
                detailStats[item.seq] = 0
              }
            }),
          )
          setPeriodStats(stats)
          setDetailStats(detailStats)
        }
        fetchPeriodAndDetailStats()
      })
      .catch((err) => {
        alert(err)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (items.length === 0) return null

  // 차트 데이터 가공
  const chartData = [...items]
    .map((item) => ({
      name: String(item.dataCrtYm),
      total: detailStats[item.seq] ?? 0, // seq별 전체 인원
      join: periodStats[item.seq]?.join ?? 0,
      leave: periodStats[item.seq]?.leave ?? 0,
    }))
    .reverse()

  const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({
    active,
    payload,
    label,
  }) => {
    if (!active || !payload || payload.length === 0) return null
    const join = payload.find((p) => p.dataKey === 'join')
    const leave = payload.find((p) => p.dataKey === 'leave')
    const total = payload.find((p) => p.dataKey === 'total')
    return (
      <CustomTooltipContainer>
        <SmallText>
          총 인원 <b>{total?.value ?? '-'}명</b>
        </SmallText>
        <SmallText>
          입사 <b>{join?.value ?? '-'}명</b>
        </SmallText>
        <SmallText>
          퇴사 <b>{leave?.value ?? '-'}명</b>
        </SmallText>
        <SmallText>{dayjs(label as string).format('YYYY.MM')}</SmallText>
      </CustomTooltipContainer>
    )
  }

  return (
    <SubSectionWrapper>
      <Row style={{ justifyContent: 'space-between' }}>
        <SubSectionTitle>Q. 최근 1년간 인원 변화가 궁금해요.</SubSectionTitle>
        <Button
          variant={BtnType.SUPER_LOW_STRONG}
          style={{ width: 'fit-content' }}
          size={BtnSize.SMALL}
          onClick={() => {
            window.open(CHANNEL_TALK_URL, '_blank')
          }}
        >
          {isDesktop ? '인원정보' : ''} 오류 신고
        </Button>
      </Row>
      <ChartContainer>
        <ResponsiveContainer width='100%' height='100%'>
          <ComposedChart data={chartData}>
            <CartesianGrid stroke={theme.color.background} />
            <YAxis
              stroke={theme.color.background}
              width={30}
              tick={(props) => {
                const { x, y, payload, index } = props
                // 짝수 index만 표시
                if (index % 2 !== 0) return <g />
                return (
                  <text
                    x={x}
                    y={y}
                    textAnchor='end'
                    fontSize={theme.fontSize.small}
                    fill={theme.color.gray2}
                  >
                    {payload.value}명
                  </text>
                )
              }}
            />
            <XAxis
              dataKey='name'
              stroke={theme.color.background}
              tick={(props) => {
                const { x, y, payload, index } = props
                // 짝수 index만 표시
                if (index % 2 !== 0) return <g />
                return (
                  <text
                    x={x}
                    y={y + 16}
                    textAnchor='middle'
                    fontSize={theme.fontSize.small}
                    fill={theme.color.gray2}
                  >
                    {dayjs(payload.value).format('YYYY.MM')}
                  </text>
                )
              }}
            />
            <Tooltip content={CustomTooltip} />
            <Bar
              dataKey='join'
              barSize={20}
              fill={theme.color.secondary}
              name='입사자'
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey='leave'
              barSize={20}
              fill={theme.color.line}
              name='퇴사자'
              radius={[4, 4, 0, 0]}
            />
            <Line
              dot={false}
              dataKey='total'
              stroke={theme.color.primary}
              name='총 인원'
              strokeWidth={1.5}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartContainer>
      <BaseText style={{ color: theme.color.gray1, textAlign: 'end' }}>
        출처: 공공데이터포털
      </BaseText>
    </SubSectionWrapper>
  )
}

const CustomTooltipContainer = styled(Column)`
  gap: 4px;
  background: #fff;
  border: 1px solid ${(p) => p.theme.color.line};
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  text-align: center;
`

const ChartContainer = styled(Column)`
  gap: 24px;
  width: 100%;
  height: 250px;
  padding: 24px 16px 12px 12px;
  border: 1px solid ${(p) => p.theme.color.background};
  border-radius: 6px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 16px 12px 8px 8px;
  }
`
